import { lazy, Suspense } from "react";
import {
  createBrowserRouter,
  Navigate,
  RouterProvider,
} from "react-router-dom";
import MainLayout from "@/components/layout/MainLayout";
import AdminLayout from "@/components/layout/AdminLayout";
import LanguageRoute from "./LanguageRoute";
import ProtectedRoute from "./ProtectedRoute";
import AdminRoute from "./AdminRoute";
import { ErrorBoundary } from "@/components/common/ErrorBoundary";
import BookSpinner from "@/components/common/BookSpinner";

// ─── Lazy-loaded pages ───────────────────────────────────────────────────────

const Home = lazy(() => import("@/pages/Home"));
const Catalog = lazy(() => import("@/pages/Catalog"));
const ThesisDetailPage = lazy(() => import("@/pages/ThesisDetailPage"));
const ReaderPage = lazy(() => import("@/pages/ReaderPage"));
const AuthorsDirectory = lazy(() => import("@/pages/AuthorsDirectory"));
const AuthorProfilePage = lazy(() => import("@/pages/AuthorProfilePage"));
const AuthorCVPage = lazy(() => import("@/pages/AuthorCVPage"));
const About = lazy(() => import("@/pages/About"));
const Contact = lazy(() => import("@/pages/Contact"));
const FAQ = lazy(() => import("@/pages/FAQ"));
const Login = lazy(() => import("@/pages/Login"));
const Register = lazy(() => import("@/pages/Register"));
const NotFound = lazy(() => import("@/pages/NotFound"));

// Phase 3: User feature pages
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const Bookmarks = lazy(() => import("@/pages/Bookmarks"));
const ReadingHistory = lazy(() => import("@/pages/ReadingHistory"));
const NotificationCenter = lazy(() => import("@/pages/NotificationCenter"));
const Profile = lazy(() => import("@/pages/Profile"));
const Preferences = lazy(() => import("@/pages/Preferences"));
const MyAuthorProfile = lazy(() => import("@/pages/MyAuthorProfile"));

// Admin pages (Phase 2)
const ManageTheses = lazy(() => import("@/pages/admin/ManageTheses"));
const AddThesis = lazy(() => import("@/pages/admin/AddThesis"));
const EditThesis = lazy(() => import("@/pages/admin/EditThesis"));
const ManageAuthors = lazy(() => import("@/pages/admin/ManageAuthors"));
const AddAuthor = lazy(() => import("@/pages/admin/AddAuthor"));
const EditAuthor = lazy(() => import("@/pages/admin/EditAuthor"));
const ManageDepartments = lazy(() => import("@/pages/admin/ManageDepartments"));
const ManageCategories = lazy(() => import("@/pages/admin/ManageCategories"));

// Admin pages (Phase 3)
const ManageAuthorClaims = lazy(
  () => import("@/pages/admin/ManageAuthorClaims"),
);
const ModerateReviews = lazy(() => import("@/pages/admin/ModerateReviews"));
const SendNotification = lazy(() => import("@/pages/admin/SendNotification"));

// Admin pages (Phase 4)
const ManagePartners = lazy(() => import("@/pages/admin/ManagePartners"));
const AdminDashboard = lazy(() => import("@/pages/admin/AdminDashboard"));
const ManageUsers = lazy(() => import("@/pages/admin/ManageUsers"));
const AuditLog = lazy(() => import("@/pages/admin/AuditLog"));
const Reports = lazy(() => import("@/pages/admin/Reports"));
const Settings = lazy(() => import("@/pages/admin/Settings"));

// Auth pages (Phase 4)
const ForgotPassword = lazy(() => import("@/pages/ForgotPassword"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));

// ─── KATALOG v2 pages ────────────────────────────────────────────────────────

const Sections = lazy(() => import("@/pages/Sections"));
const SectionDetail = lazy(() => import("@/pages/SectionDetail"));
const DocumentDetail = lazy(() => import("@/pages/DocumentDetail"));
const Institutions = lazy(() => import("@/pages/Institutions"));
const InstitutionDetail = lazy(() => import("@/pages/InstitutionDetail"));
const Countries = lazy(() => import("@/pages/Countries"));
const CountryDetail = lazy(() => import("@/pages/CountryDetail"));
const ClassicAuthors = lazy(() => import("@/pages/ClassicAuthors"));
const ClassicAuthorDetail = lazy(() => import("@/pages/ClassicAuthorDetail"));

// Admin pages (KATALOG v2)
const ManageSections = lazy(() => import("@/pages/admin/ManageSections"));
const ManageSubdivisions = lazy(
  () => import("@/pages/admin/ManageSubdivisions"),
);
const ManageDocuments = lazy(() => import("@/pages/admin/ManageDocuments"));
const AddDocument = lazy(() => import("@/pages/admin/AddDocument"));
const EditDocument = lazy(() => import("@/pages/admin/EditDocument"));
const ManageInstitutions = lazy(
  () => import("@/pages/admin/ManageInstitutions"),
);
const ManageCountries = lazy(() => import("@/pages/admin/ManageCountries"));

// ─── Loading fallback ────────────────────────────────────────────────────────

function PageLoader() {
  return (
    <div className="flex min-h-[60vh] items-center justify-center">
      <BookSpinner />
    </div>
  );
}

// ─── Router definition ───────────────────────────────────────────────────────

const router = createBrowserRouter([
  // Root redirect: "/" → "/fr" (default language)
  {
    path: "/",
    element: <Navigate to="/fr" replace />,
  },

  // Language-prefixed routes: /:lang/*
  {
    path: "/:lang",
    element: <LanguageRoute />,
    errorElement: (
      <ErrorBoundary>
        <NotFound />
      </ErrorBoundary>
    ),
    children: [
      // ── Public routes (MainLayout) ─────────────────────────────────
      {
        element: <MainLayout />,
        children: [
          // Home
          {
            index: true,
            element: (
              <Suspense fallback={<PageLoader />}>
                <Home />
              </Suspense>
            ),
          },

          // Catalog
          {
            path: "catalog",
            element: (
              <Suspense fallback={<PageLoader />}>
                <Catalog />
              </Suspense>
            ),
          },

          // Thesis detail
          {
            path: "catalog/:id",
            element: (
              <Suspense fallback={<PageLoader />}>
                <ThesisDetailPage />
              </Suspense>
            ),
          },

          // Secure thesis reader (requires auth — handled inside component)
          {
            path: "catalog/:id/read",
            element: (
              <Suspense fallback={<PageLoader />}>
                <ReaderPage />
              </Suspense>
            ),
          },

          // Authors directory
          {
            path: "authors",
            element: (
              <Suspense fallback={<PageLoader />}>
                <AuthorsDirectory />
              </Suspense>
            ),
          },

          // Author profile
          {
            path: "authors/:id",
            element: (
              <Suspense fallback={<PageLoader />}>
                <AuthorProfilePage />
              </Suspense>
            ),
          },

          // Author CV reader (requires auth — handled inside component)
          {
            path: "authors/:id/cv",
            element: (
              <Suspense fallback={<PageLoader />}>
                <AuthorCVPage />
              </Suspense>
            ),
          },

          // About
          {
            path: "about",
            element: (
              <Suspense fallback={<PageLoader />}>
                <About />
              </Suspense>
            ),
          },

          // Contact
          {
            path: "contact",
            element: (
              <Suspense fallback={<PageLoader />}>
                <Contact />
              </Suspense>
            ),
          },

          // FAQ
          {
            path: "faq",
            element: (
              <Suspense fallback={<PageLoader />}>
                <FAQ />
              </Suspense>
            ),
          },

          // ── Auth pages ─────────────────────────────────────────────
          {
            path: "login",
            element: (
              <Suspense fallback={<PageLoader />}>
                <Login />
              </Suspense>
            ),
          },
          {
            path: "register",
            element: (
              <Suspense fallback={<PageLoader />}>
                <Register />
              </Suspense>
            ),
          },
          {
            path: "forgot-password",
            element: (
              <Suspense fallback={<PageLoader />}>
                <ForgotPassword />
              </Suspense>
            ),
          },
          {
            path: "reset-password",
            element: (
              <Suspense fallback={<PageLoader />}>
                <ResetPassword />
              </Suspense>
            ),
          },

          // ── KATALOG v2 public routes ──────────────────────────────────────

          // Sections catalog
          {
            path: "sections",
            element: (
              <Suspense fallback={<PageLoader />}>
                <Sections />
              </Suspense>
            ),
          },
          {
            path: "sections/:slug",
            element: (
              <Suspense fallback={<PageLoader />}>
                <SectionDetail />
              </Suspense>
            ),
          },

          // Document detail
          {
            path: "documents/:id",
            element: (
              <Suspense fallback={<PageLoader />}>
                <DocumentDetail />
              </Suspense>
            ),
          },

          // Institutions
          {
            path: "institutions",
            element: (
              <Suspense fallback={<PageLoader />}>
                <Institutions />
              </Suspense>
            ),
          },
          {
            path: "institutions/:id",
            element: (
              <Suspense fallback={<PageLoader />}>
                <InstitutionDetail />
              </Suspense>
            ),
          },

          // Countries
          {
            path: "countries",
            element: (
              <Suspense fallback={<PageLoader />}>
                <Countries />
              </Suspense>
            ),
          },
          {
            path: "countries/:code",
            element: (
              <Suspense fallback={<PageLoader />}>
                <CountryDetail />
              </Suspense>
            ),
          },

          // Classic Authors
          {
            path: "classic-authors",
            element: (
              <Suspense fallback={<PageLoader />}>
                <ClassicAuthors />
              </Suspense>
            ),
          },
          {
            path: "classic-authors/:id",
            element: (
              <Suspense fallback={<PageLoader />}>
                <ClassicAuthorDetail />
              </Suspense>
            ),
          },

          // ── Protected user routes (Phase 3) ────────────────────────
          {
            element: <ProtectedRoute />,
            children: [
              {
                path: "dashboard",
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <Dashboard />
                  </Suspense>
                ),
              },
              {
                path: "bookmarks",
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <Bookmarks />
                  </Suspense>
                ),
              },
              {
                path: "history",
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <ReadingHistory />
                  </Suspense>
                ),
              },
              {
                path: "notifications",
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <NotificationCenter />
                  </Suspense>
                ),
              },
              {
                path: "profile",
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <Profile />
                  </Suspense>
                ),
              },
              {
                path: "profile/preferences",
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <Preferences />
                  </Suspense>
                ),
              },
              {
                path: "profile/author",
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <MyAuthorProfile />
                  </Suspense>
                ),
              },
            ],
          },

          // ── 404 catch-all (within language) ────────────────────────
          {
            path: "*",
            element: (
              <Suspense fallback={<PageLoader />}>
                <NotFound />
              </Suspense>
            ),
          },
        ],
      },

      // ── Admin routes (AdminLayout) ─────────────────────────────────
      {
        path: "admin",
        element: <AdminRoute />,
        children: [
          {
            element: <AdminLayout />,
            children: [
              {
                index: true,
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <AdminDashboard />
                  </Suspense>
                ),
              },
              {
                path: "theses",
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <ManageTheses />
                  </Suspense>
                ),
              },
              {
                path: "theses/new",
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <AddThesis />
                  </Suspense>
                ),
              },
              {
                path: "theses/:id/edit",
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <EditThesis />
                  </Suspense>
                ),
              },
              {
                path: "authors",
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <ManageAuthors />
                  </Suspense>
                ),
              },
              {
                path: "authors/new",
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <AddAuthor />
                  </Suspense>
                ),
              },
              {
                path: "authors/:id/edit",
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <EditAuthor />
                  </Suspense>
                ),
              },
              {
                path: "authors/claims",
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <ManageAuthorClaims />
                  </Suspense>
                ),
              },
              {
                path: "users",
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <ManageUsers />
                  </Suspense>
                ),
              },
              {
                path: "partners",
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <ManagePartners />
                  </Suspense>
                ),
              },
              {
                path: "departments",
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <ManageDepartments />
                  </Suspense>
                ),
              },
              {
                path: "categories",
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <ManageCategories />
                  </Suspense>
                ),
              },
              {
                path: "notifications",
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <SendNotification />
                  </Suspense>
                ),
              },
              {
                path: "reviews",
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <ModerateReviews />
                  </Suspense>
                ),
              },
              {
                path: "reports",
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <Reports />
                  </Suspense>
                ),
              },
              {
                path: "audit-log",
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <AuditLog />
                  </Suspense>
                ),
              },
              {
                path: "settings",
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <Settings />
                  </Suspense>
                ),
              },

              // ── KATALOG v2 admin routes ──────────────────────────────────

              // Sections management
              {
                path: "sections",
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <ManageSections />
                  </Suspense>
                ),
              },
              {
                path: "sections/:slug/subdivisions",
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <ManageSubdivisions />
                  </Suspense>
                ),
              },

              // Documents management
              {
                path: "documents",
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <ManageDocuments />
                  </Suspense>
                ),
              },
              {
                path: "documents/new",
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <AddDocument />
                  </Suspense>
                ),
              },
              {
                path: "documents/:id/edit",
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <EditDocument />
                  </Suspense>
                ),
              },

              // Institutions management
              {
                path: "institutions",
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <ManageInstitutions />
                  </Suspense>
                ),
              },

              // Countries management
              {
                path: "countries",
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <ManageCountries />
                  </Suspense>
                ),
              },
              // Admin catch-all
              {
                path: "*",
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <NotFound />
                  </Suspense>
                ),
              },
            ],
          },
        ],
      },
    ],
  },

  // Global catch-all: redirect unknown paths to /fr/
  {
    path: "*",
    element: <Navigate to="/fr" replace />,
  },
]);

// ─── Exported component ──────────────────────────────────────────────────────

export default function AppRouter() {
  return <RouterProvider router={router} />;
}
